import { ref, watch, type Ref } from 'vue';
import { IMnemonicService } from '@lingvo-news/contracts';
import { getVueEnv } from '../infrastructure/vue-env';

export interface WordMnemonic {
	imageUrl: string;
	description: string;
}

/**
 * Fetches mnemonics for a list of word IDs and returns a reactive map.
 * Re-fetches when the word IDs change.
 */
export function useWordMnemonics(wordIds: Ref<string[]>) {
	const mnemonicsByWordId = ref<Map<string, WordMnemonic>>(new Map());
	const isLoading = ref(false);

	watch(
		wordIds,
		async (ids) => {
			const uniqueIds = [...new Set(ids)].filter(Boolean);
			if (uniqueIds.length === 0) {
				mnemonicsByWordId.value = new Map();
				return;
			}

			try {
				isLoading.value = true;
				const env = getVueEnv();
				const mnemonicService = env.get(IMnemonicService);
				const response = await mnemonicService.getMnemonics({ wordIds: uniqueIds });
				const map = new Map<string, WordMnemonic>();
				for (const item of response.items) {
					if (item.wordId && (item.imageUrl || item.description)) {
						map.set(item.wordId, {
							imageUrl: item.imageUrl ?? '',
							description: item.description ?? ''
						});
					}
				}
				mnemonicsByWordId.value = map;
			} catch (err) {
				console.warn('Failed to load mnemonics for word list', err);
				mnemonicsByWordId.value = new Map();
			} finally {
				isLoading.value = false;
			}
		},
		{ immediate: true }
	);

	return { mnemonicsByWordId, isLoading };
}
